import { RefObject, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { errorMessages } from '~/constants/errorMessages.ts';
import {
  applyFilesDiiaSign,
  getFilesDiiaSignStatus,
  getFilesHashesToSign,
  initializeFilesDiiaSign,
  saveFilesSignedDocuments,
} from '~/store/guarantees/thunks/filesSigningThunks.ts';
import { resetIITLib } from '~/libs/iit/lib.js';
import { dismissIITSigning } from '~/store/iit/slices.ts';
import type { TIITSign } from '~/store/iit/types.ts';
import type { TFilesSigningSaveSignedDocumentsResponse } from '~/store/guarantees/types.ts';
import type { AppDispatch } from '~/store/store.ts';
import {
  FILES_SIGNING_STATUS_POLL_ATTEMPTS,
  FILES_SIGNING_STATUS_POLL_INTERVAL_MS,
} from './constants.ts';
import {
  canFinalizeFilesSigningDiiaStatus,
  getFilesSigningDiiaFailureMessage,
  getFilesSigningErrorMessage,
  isFilesSigningDiiaFailureStatus,
  isSuccessfulFilesSigningDiiaApplyStatus,
} from './utils.ts';

export const useFilesSigning = ({
  fileIds,
  applicationId,
  onSuccess,
  onError,
  setDeepLink,
  timerRef,
}: {
  fileIds: string[];
  applicationId?: string;
  onSuccess: (saveResponse?: TFilesSigningSaveSignedDocumentsResponse) => Promise<void> | void;
  onError: (message: string) => void;
  setDeepLink: (link: string) => void;
  timerRef?: RefObject<{ startTimer: () => void; resetTimer: () => void } | null>;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const diiaAttemptRef = useRef(0);
  const activeStatusRequestRef = useRef<{ abort: () => void } | null>(null);
  const fileIdsKey = fileIds.join('|');

  const isAttemptActive = (attemptId: number) => diiaAttemptRef.current === attemptId;

  const cancelActiveDiiaAttempt = () => {
    diiaAttemptRef.current += 1;
    activeStatusRequestRef.current?.abort();
    activeStatusRequestRef.current = null;
  };

  const resetSigningSession = () => {
    cancelActiveDiiaAttempt();
    setDeepLink('');
    timerRef?.current?.resetTimer();
  };

  const handleDiiaFailure = (message: string, attemptId?: number) => {
    if (attemptId != null && !isAttemptActive(attemptId)) {
      return;
    }

    resetSigningSession();
    onError(message);
  };

  const getRequiredApplicationId = (): string | null => {
    if (applicationId) {
      return applicationId;
    }

    onError(errorMessages.SERVICE_UNAVAILABLE);
    return null;
  };

  const waitForStatusToFinalize = async (
    requestId: string,
    signingApplicationId: string,
    attemptId: number
  ) => {
    let lastStatus: string | null = null;

    for (let attempt = 0; attempt < FILES_SIGNING_STATUS_POLL_ATTEMPTS; attempt += 1) {
      if (!isAttemptActive(attemptId)) {
        return null;
      }

      const statusRequest = dispatch(
        getFilesDiiaSignStatus({ applicationId: signingApplicationId, requestId })
      );
      activeStatusRequestRef.current = statusRequest;

      let statusResponse;

      try {
        statusResponse = await statusRequest.unwrap();
      } finally {
        if (activeStatusRequestRef.current === statusRequest) {
          activeStatusRequestRef.current = null;
        }
      }

      if (!isAttemptActive(attemptId)) {
        return null;
      }

      const status = statusResponse.status as string;
      lastStatus = status;

      if (
        canFinalizeFilesSigningDiiaStatus(status) ||
        isFilesSigningDiiaFailureStatus(status)
      ) {
        return status;
      }

      if (attempt < FILES_SIGNING_STATUS_POLL_ATTEMPTS - 1) {
        await new Promise((resolve) =>
          setTimeout(resolve, FILES_SIGNING_STATUS_POLL_INTERVAL_MS)
        );
      }
    }

    return lastStatus;
  };

  const handleIITSignature = async () => {
    if (!fileIds.length) {
      onError(errorMessages.NO_FILES_TO_SIGN);
      return;
    }

    try {
      resetIITLib();
      dispatch(dismissIITSigning());
      await dispatch(getFilesHashesToSign({ fileIds })).unwrap();
    } catch (error) {
      onError(getFilesSigningErrorMessage(error));
    }
  };

  const onIITSign = async (signFiles: TIITSign[]) => {
    if (!fileIds.length) {
      onError(errorMessages.NO_FILES_TO_SIGN);
      return;
    }

    const signingApplicationId = getRequiredApplicationId();

    if (!signingApplicationId) {
      return;
    }

    try {
      const saveResponse = await dispatch(
        saveFilesSignedDocuments({
          applicationId: signingApplicationId,
          fileList: signFiles,
        })
      ).unwrap();

      await onSuccess(saveResponse);
    } catch (error) {
      onError(getFilesSigningErrorMessage(error) || errorMessages.DOCUMENT_SIGNING_FAILED);
    }
  };

  const handleDiiaSignature = async (): Promise<boolean> => {
    cancelActiveDiiaAttempt();
    const attemptId = diiaAttemptRef.current;
    setDeepLink('');

    try {
      if (!fileIds.length) {
        handleDiiaFailure(errorMessages.NO_FILES_TO_SIGN, attemptId);
        return false;
      }

      const signingApplicationId = getRequiredApplicationId();

      if (!signingApplicationId) {
        return false;
      }

      const { requestId, deeplink, dynamicLink } = await dispatch(
        initializeFilesDiiaSign({ fileIds, applicationId: signingApplicationId })
      ).unwrap();

      if (!isAttemptActive(attemptId)) {
        return false;
      }

      setDeepLink(deeplink || dynamicLink || '');
      timerRef?.current?.startTimer();

      void (async () => {
        const statusToFinalize = await waitForStatusToFinalize(
          requestId,
          signingApplicationId,
          attemptId
        );

        if (!isAttemptActive(attemptId)) {
          return;
        }

        if (
          !statusToFinalize ||
          isFilesSigningDiiaFailureStatus(statusToFinalize) ||
          !canFinalizeFilesSigningDiiaStatus(statusToFinalize)
        ) {
          handleDiiaFailure(getFilesSigningDiiaFailureMessage(statusToFinalize), attemptId);
          return;
        }

        const applyResponse = await dispatch(
          applyFilesDiiaSign({ applicationId: signingApplicationId, requestId })
        ).unwrap();

        if (!isAttemptActive(attemptId)) {
          return;
        }

        const finalStatus = applyResponse.status as string | undefined;

        if (!isSuccessfulFilesSigningDiiaApplyStatus(finalStatus)) {
          handleDiiaFailure(errorMessages.DOCUMENT_SIGNING_FAILED, attemptId);
          return;
        }

        resetSigningSession();
        await onSuccess();
      })().catch((error) => {
        handleDiiaFailure(getFilesSigningErrorMessage(error), attemptId);
      });

      return true;
    } catch (error) {
      handleDiiaFailure(getFilesSigningErrorMessage(error), attemptId);
      return false;
    }
  };

  useEffect(() => {
    return () => {
      diiaAttemptRef.current += 1;
      activeStatusRequestRef.current?.abort();
      activeStatusRequestRef.current = null;
    };
  }, [applicationId, fileIdsKey]);

  return {
    handleIITSignature,
    onIITSign,
    handleDiiaSignature,
    resetSigningSession,
  };
};
